import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { client } from 'src/graphql/client';
import { queryFresh } from 'src/graphql/queryFresh';
import { GET_ALL_STOP_STATUSES } from 'src/graphql/requests/getAllStopStatuses';
import { GetAllStopStatusesRoute, Order } from 'src/graphql/types/delivery';
import {
  GDeliveryStatusDmp,
  GGetAllStopStatusesQuery,
  GLocationMembershipTier,
  GOrderDeliveryMethodEnum,
} from 'src/graphql/types/generated';
import { isDefined } from 'src/utils';
import { isOutsideDeliveryWindow as isOrderOutsideDeliveryWindow } from 'src/utils/order';
import {
  deriveStopIconFlags,
  doesStopHaveInfo,
  foundDeliveryNote,
  foundIssues,
  foundNotes,
} from 'src/utils/secondaryStopStatus';
import { DeliveryState, RouteStatusState, StopStatusState } from './state';

// The `tasks` field isn't in the generated schema until the backend ships to
// stage, so read it through a cast (see NOTES cross-repo codegen note).
type StopTask = { status?: string | null };
const stopTasks = (stop: unknown): StopTask[] | null =>
  (stop as { tasks?: StopTask[] | null }).tasks ?? null;

export const getAllStopStatuses = createAsyncThunk(
  'delivery/getAllStopStatuses',
  async ({
    warehouseId,
    nightEnd,
  }: {
    warehouseId: string;
    nightEnd: string;
  }): Promise<GetAllStopStatusesRoute[]> => {
    const { data, error } = await queryFresh<GGetAllStopStatusesQuery>(
      client,
      GET_ALL_STOP_STATUSES,
      { input: { warehouseId, nightEnd } },
    );
    if (error || !data?.routes) {
      throw new Error(error?.message || 'No stop statuses returned');
    }
    return data.routes;
  },
);

export const getAllStopStatusesFulfilled = (
  state: DeliveryState,
  action: PayloadAction<GetAllStopStatusesRoute[]> & {
    meta?: {
      requestId?: string;
      arg?: { warehouseId: string; nightEnd: string };
    };
  },
): void => {
  if (
    action.meta?.requestId &&
    state.latestStopStatusesRequestId &&
    action.meta.requestId !== state.latestStopStatusesRequestId
  )
    return;
  if (
    action.meta?.arg &&
    (action.meta.arg.warehouseId !== state.selectedWarehouseId ||
      action.meta.arg.nightEnd !== state.nightEnd.viewing)
  )
    return;
  const routes = [...action.payload];
  let routeStatuses: RouteStatusState[] = [];
  let stopStatuses: StopStatusState[] = [];
  let redeliveryStopStatuses: StopStatusState[] = [];
  // eslint-disable-next-line complexity
  routes.forEach((route) => {
    const selectedWarehouse = state.warehouses?.find(
      (warehouse) => warehouse.id === state.selectedWarehouseId,
    );
    const stops = route.stops ?? [];
    // eslint-disable-next-line complexity
    const myStopStatuses: StopStatusState[] = stops.map((stop) => {
      const hasRepeatedAccessIssues =
        stop?.__typename === 'Delivery' ? stop.hasRepeatedAccessIssues : false;
      const isFirstDelivery =
        stop?.__typename === 'Delivery' ? stop.isFirstDelivery : false;
      const orderWithWarehouseNote = stop.orders?.some((order) => {
        return order.warehouseNote;
      });
      const myIsOutsideDeliveryWindow = stop.orders?.some((order) => {
        return isOrderOutsideDeliveryWindow({
          order: order as Order,
          timeZone: selectedWarehouse?.timeZone,
          viewingNightEnd: state.nightEnd.viewing,
        });
      });
      const hasPastryOrders: boolean =
        (stop?.__typename === 'Delivery' &&
          stop?.orders?.some((order) => {
            return !order.vendor.isWarehouse;
          })) ??
        false;
      const iconFlags = deriveStopIconFlags(
        stop?.__typename === 'Delivery' ? stop.orders : [],
      );
      const isClosed: boolean =
        (stop?.__typename === 'Delivery' &&
          stop?.orders?.some((order) => {
            return (
              order.location.scheduledDeliveryTimeSource === 'STORE_HOURS' &&
              !order.scheduledDeliveryTime
            );
          })) ??
        false;
      const hasMetrobiOrder: boolean =
        (stop?.__typename === 'Delivery' &&
          stop?.orders?.some((order) => {
            return (
              order.deliveryMethod === GOrderDeliveryMethodEnum.MetrobiDelivery
            );
          })) ??
        false;
      return {
        routeId: route.id,
        stopId: stop.id,
        stopStatus: stop.deliveryStatusDmp,
        hasPastryOrders: hasPastryOrders,
        stopSecondaryStatus: {
          hasRepeatedIssues: hasRepeatedAccessIssues,
          hasIssues: foundIssues(stop),
          hasIssueNotes: foundNotes(stop),
          hasDeliveryNote: foundDeliveryNote(stop),
          hasWarehouseNote: orderWithWarehouseNote ?? false,
          hasReminder: !!stop.reminderMessage,
          hasTasks: (stopTasks(stop)?.length ?? 0) > 0,
          hasIncompleteTask:
            stopTasks(stop)?.some((task) => task.status === 'incomplete') ??
            false,
          isOutsideDeliveryWindow: myIsOutsideDeliveryWindow ?? false,
          isFirstDelivery: isFirstDelivery,
          isRefrigerationRequired: iconFlags.isRefrigerationRequired,
          isFreezerRequired: iconFlags.isFreezerRequired,
          hasBienCuitOrder: iconFlags.hasBienCuitOrder,
          hasPastryOrder: iconFlags.hasPastryOrder,
          hasTrackedVendorOrder: iconFlags.hasTrackedVendorOrder,
          isClosed: isClosed,
          hasMetrobiOrder: hasMetrobiOrder,
          isMetrobiIneligible:
            stop.__typename === 'Delivery' &&
            !!stop.location?.effectivelyExcluded &&
            !hasMetrobiOrder,
          hasRefrigeratedDairy: iconFlags.hasRefrigeratedDairy,
        },
      };
    });

    const redeliveryStops = route.redeliveryStops ?? [];
    const myRedeliveryStopStatuses: StopStatusState[] = redeliveryStops.map(
      (redeliveryStop) => {
        const hasRepeatedAccessIssues = redeliveryStop.hasRepeatedAccessIssues;
        const hasPastryOrders: boolean =
          redeliveryStop?.orders?.some((order) => {
            return !order.vendor.isWarehouse;
          }) ?? false;
        const orderWithWarehouseNote =
          redeliveryStop.orders?.some((order) => {
            return order.warehouseNote;
          }) ?? false;
        const myIsOutsideDeliveryWindow = redeliveryStop.orders?.some(
          (order) => {
            return isOrderOutsideDeliveryWindow({
              order: order as Order,
              timeZone: selectedWarehouse?.timeZone,
              viewingNightEnd: state.nightEnd.viewing,
            });
          },
        );
        const iconFlags = deriveStopIconFlags(redeliveryStop.orders);
        const isClosed: boolean =
          redeliveryStop.orders?.some((order) => {
            return (
              order.location.scheduledDeliveryTimeSource === 'STORE_HOURS' &&
              !order.scheduledDeliveryTime
            );
          }) ?? false;
        const hasMetrobiOrder: boolean =
          redeliveryStop.orders?.some((order) => {
            return (
              order.deliveryMethod === GOrderDeliveryMethodEnum.MetrobiDelivery
            );
          }) ?? false;
        return {
          routeId: route.id,
          stopId: redeliveryStop.id,
          stopStatus: redeliveryStop.deliveryStatusDmp,
          hasPastryOrders: hasPastryOrders,
          stopSecondaryStatus: {
            hasDeliveryNote: foundDeliveryNote(redeliveryStop),
            hasIssues: foundIssues(redeliveryStop),
            hasIssueNotes: foundNotes(redeliveryStop),
            hasRepeatedIssues: hasRepeatedAccessIssues,
            hasWarehouseNote: orderWithWarehouseNote,
            hasReminder: !!redeliveryStop.reminderMessage,
            // Redelivery stops don't carry additional tasks in v1 (delivery-only).
            hasTasks: false,
            hasIncompleteTask: false,
            isOutsideDeliveryWindow: myIsOutsideDeliveryWindow ?? false,
            isFirstDelivery: false,
            isRefrigerationRequired: iconFlags.isRefrigerationRequired,
            isFreezerRequired: iconFlags.isFreezerRequired,
            hasBienCuitOrder: iconFlags.hasBienCuitOrder,
            hasPastryOrder: iconFlags.hasPastryOrder,
            hasTrackedVendorOrder: iconFlags.hasTrackedVendorOrder,
            isClosed: isClosed,
            hasMetrobiOrder: hasMetrobiOrder,
            isMetrobiIneligible:
              !!redeliveryStop.location?.effectivelyExcluded &&
              !hasMetrobiOrder,
            hasRefrigeratedDairy: iconFlags.hasRefrigeratedDairy,
          },
        };
      },
    );

    // Delivery stops
    const inTransitStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.InTransit,
      ) ?? [];
    const partiallyDeliveredStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.PartiallyDelivered,
      ) ?? [];
    const undeliveredStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.Undelivered,
      ) ?? [];
    const goldStops =
      stops.filter((stop) => {
        if (stop.__typename === 'Delivery')
          return (
            stop.orders?.[0]?.location.membershipTier ===
            GLocationMembershipTier.Gold
          );
        return false;
      }) ?? [];
    const silverStops =
      stops.filter((stop) => {
        if (stop.__typename === 'Delivery')
          return (
            stop.orders?.[0]?.location.membershipTier ===
            GLocationMembershipTier.Silver
          );
        return false;
      }) ?? [];
    const doorDashStops =
      stops.filter((stop) => {
        if (stop.__typename === 'Delivery')
          return stop.orders?.[0]?.location.isDoordash;
        return false;
      }) ?? [];
    const closedStops =
      stops.filter((stop) => {
        if (
          stop.__typename === 'Delivery' &&
          stop.orders?.[0].location.scheduledDeliveryTimeSource ===
            'STORE_HOURS'
        ) {
          return !isDefined(stop.orders?.[0]?.scheduledDeliveryTime);
        }
        return false;
      }) ?? [];

    // Pick up stops
    const notPickedUpStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.NotPickedUp,
      ) ?? [];
    const partiallyPickedUpStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.PartiallyPickedUp,
      ) ?? [];

    // Redelivery stops
    const inTransitRedeliveryStops =
      myRedeliveryStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.InTransit,
      ) ?? [];
    const partiallyDeliveredRedeliveryStops =
      myRedeliveryStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.PartiallyDelivered,
      ) ?? [];
    const undeliveredRedeliveryStops =
      myRedeliveryStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopStatus === GDeliveryStatusDmp.Undelivered,
      ) ?? [];
    const goldRedeliveryStops =
      redeliveryStops.filter((stop) => {
        return (
          stop.orders?.[0]?.location.membershipTier ===
          GLocationMembershipTier.Gold
        );
      }) ?? [];
    const silverRedeliveryStops =
      redeliveryStops.filter((stop) => {
        return (
          stop.orders?.[0]?.location.membershipTier ===
          GLocationMembershipTier.Silver
        );
      }) ?? [];
    const doorDashRedeliveryStops =
      redeliveryStops.filter((stop) => {
        return stop.orders?.[0]?.location.isDoordash;
      }) ?? [];
    const closedRedeliveryStops =
      redeliveryStops.filter((stop) => {
        return stop.orders?.[0]?.scheduledDeliveryTime === undefined;
      }) ?? [];

    const hasStopWithInfo =
      doesStopHaveInfo(myStopStatuses) ||
      doesStopHaveInfo(myRedeliveryStopStatuses);

    // refrigeration state stops
    const refrigeratedStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopSecondaryStatus.isRefrigerationRequired,
      ) ?? [];
    const frozenStops =
      myStopStatuses.filter(
        (myStopStatus) => myStopStatus.stopSecondaryStatus.isFreezerRequired,
      ) ?? [];
    const bienCuitStops =
      myStopStatuses.filter(
        (myStopStatus) => myStopStatus.stopSecondaryStatus.hasBienCuitOrder,
      ) ?? [];
    const pastryStops =
      myStopStatuses.filter(
        (myStopStatus) => myStopStatus.stopSecondaryStatus.hasPastryOrder,
      ) ?? [];
    const trackedVendorStops =
      myStopStatuses.filter(
        (myStopStatus) =>
          myStopStatus.stopSecondaryStatus.hasTrackedVendorOrder,
      ) ?? [];
    const metrobiStops =
      [...myStopStatuses, ...myRedeliveryStopStatuses].filter(
        (myStopStatus) => myStopStatus.stopSecondaryStatus.hasMetrobiOrder,
      ) ?? [];
    const refrigeratedDairyStops =
      myStopStatuses.filter(
        (myStopStatus) => myStopStatus.stopSecondaryStatus.hasRefrigeratedDairy,
      ) ?? [];

    const totalStops = myStopStatuses.length + myRedeliveryStopStatuses.length;
    const myRouteStatus = {
      routeId: route.id,
      totalStops,
      totalStopsVisited:
        totalStops - inTransitStops.length - inTransitRedeliveryStops.length,
      hasSecondaryStatuses: hasStopWithInfo,
      totalIncompleteTaskStops: myStopStatuses.filter(
        (myStopStatus) => myStopStatus.stopSecondaryStatus.hasIncompleteTask,
      ).length,
      totalUndeliveredStops:
        undeliveredStops.length + undeliveredRedeliveryStops.length,
      totalPartiallyDeliveredStops:
        partiallyDeliveredStops.length +
        partiallyDeliveredRedeliveryStops.length,
      totalNotPickedUpStops: notPickedUpStops.length,
      totalPartiallyPickedUpStops: partiallyPickedUpStops.length,
      totalGoldMembers: goldStops.length + goldRedeliveryStops.length,
      totalSilverMembers: silverStops.length + silverRedeliveryStops.length,
      totalDoorDashStops: doorDashStops.length + doorDashRedeliveryStops.length,
      totalRefrigerationRequired: refrigeratedStops.length,
      totalFreezerRequired: frozenStops.length,
      totalBienCuitStops: bienCuitStops.length,
      totalPastryStops: pastryStops.length,
      totalTrackedVendorStops: trackedVendorStops.length,
      totalClosedStops: closedStops.length + closedRedeliveryStops.length,
      totalMetrobiStops: metrobiStops.length,
      totalRefrigeratedDairyStops: refrigeratedDairyStops.length,
    };

    routeStatuses.push(myRouteStatus);
    stopStatuses = stopStatuses.concat(myStopStatuses);
    redeliveryStopStatuses = redeliveryStopStatuses.concat(
      myRedeliveryStopStatuses,
    );
  });

  state.deliveryStatuses = {
    routeStatuses,
    stopStatuses,
    redeliveryStopStatuses,
    loading: false,
  };
};

export const getAllStopStatusesPending = (
  state: DeliveryState,
  action: {
    meta?: {
      requestId?: string;
      arg?: { warehouseId: string; nightEnd: string };
    };
  },
): void => {
  if (
    action.meta?.arg &&
    (action.meta.arg.warehouseId !== state.selectedWarehouseId ||
      action.meta.arg.nightEnd !== state.nightEnd.viewing)
  )
    return;
  state.latestStopStatusesRequestId = action.meta?.requestId;
  state.deliveryStatuses.error = false;
  const hasCache = state.deliveryStatuses.routeStatuses.length > 0;
  state.deliveryStatuses.loading = hasCache ? false : true;
};

export const getAllStopStatusesRejected = (
  state: DeliveryState,
  action: {
    meta?: {
      requestId?: string;
      arg?: { warehouseId: string; nightEnd: string };
    };
  },
): void => {
  if (
    action.meta?.requestId &&
    state.latestStopStatusesRequestId &&
    action.meta.requestId !== state.latestStopStatusesRequestId
  )
    return;
  if (
    action.meta?.arg &&
    (action.meta.arg.warehouseId !== state.selectedWarehouseId ||
      action.meta.arg.nightEnd !== state.nightEnd.viewing)
  )
    return;
  state.deliveryStatuses.loading = false;
  state.deliveryStatuses.error = true;
};
